/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useLogProfileSubmit.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for handling form submission, validation, and deletion.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../../hooks/useI18n';
import { useAuthStore } from '../../../store/auth';
import { useNotifications } from '../../../contexts/NotificationContext';
import { createLogProfile, updateLogProfile, deleteLogProfile } from '../api';
import { LogProfileFormData, LogProfile } from '../types';

export function useLogProfileSubmit(
  mode: 'create' | 'edit',
  id: string | undefined,
  formData: LogProfileFormData,
  hasChanges: boolean,
  setOriginalData: (data: LogProfileFormData) => void,
) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user } = useAuthStore();
  const { showSuccess, showError, showInfo } = useNotifications();
  const tenantSlug = user?.tenant?.slug || t('logProfiles.defaultTenant');

  const [saving, setSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = useCallback(() => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('logProfiles.validation.logProfileNameRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData.name, t]);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent): Promise<LogProfile | undefined> => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      // Check if there are changes
      // hasChanges validation removed

      if (!validateForm()) {
        showError(t('logProfiles.validation.formErrors'));
        return undefined;
      }

      try {
        setSaving(true);

        const savedProfile =
          mode === 'create'
            ? await createLogProfile(formData, tenantSlug, user?.id)
            : await updateLogProfile(id!, formData, tenantSlug, user?.id);

        // Show success notification
        showSuccess(
          mode === 'create' ? t('logProfiles.status.created') : t('logProfiles.status.updated'),
        );

        // Update originalData after successful save (deep copy to avoid shared references)
        setOriginalData({
          name: formData.name,
          description: formData.description,
          permissions: formData.permissions.map((p) => ({ ...p })),
        });

        return savedProfile;
      } catch (error) {
        let errorMessage = error instanceof Error ? error.message : t('logProfiles.errors.unknownError');
        
        // Check for duplicate name error
        if (errorMessage.includes('already exists') || errorMessage.includes('Unique constraint')) {
          setErrors({ name: t('logProfiles.errors.nameExists') });
          errorMessage = t('logProfiles.errors.nameExists');
        }
        
        showError(`${t('logProfiles.errors.errorSaving')}: ${errorMessage}`);
        return undefined;
      } finally {
        setSaving(false);
      }
    },
    [mode, id, formData, hasChanges, validateForm, tenantSlug, user?.id, showSuccess, showError, showInfo, t, setOriginalData],
  );

  const handleDelete = useCallback(async () => {
    if (mode !== 'edit' || !id || id === 'new') return;

    try {
      setIsDeleting(true);
      await deleteLogProfile(id, tenantSlug, user?.id);

      // Show success notification briefly before navigating
      showSuccess(t('logProfiles.status.deleted'));

      // Navigate back to list after successful delete
      setTimeout(() => {
        navigate('/log-profiles');
      }, 500);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : t('logProfiles.errors.deleteFailed');
      showError(errorMessage);
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  }, [mode, id, tenantSlug, user?.id, showSuccess, showError, t, navigate]);

  const handleDeleteClick = useCallback(() => {
    setShowDeleteModal(true);
  }, []);

  const handleCancel = useCallback(() => {
    setShowDeleteModal(false);
  }, []);

  return {
    saving,
    isDeleting,
    showDeleteModal,
    errors,
    setErrors,
    handleSubmit,
    handleDelete,
    handleDeleteClick,
    handleCancel,
  };
}

